import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Row,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";
import Select from "react-select";
import CategoryServices from "../../../services/OrderingServices/CategoryService";
import SalesReportService from "../../../services/OrderingServices/SalesReportService";
import NumberWithCommas from "../../../pages/Pages/Starter/NumberWithCommas";
import PieChart from "../../../Components/Common/Piechart";

// ------------------ UNIQUE COLOR PALETTE -------------------
const colorPalette = [
  "#008FFB",
  "#00E396",
  "#FEB019",
  "#FF4560",
  "#775DD0",
  "#3F51B5",
  "#4CAF50",
  "#F86624",
  "#2B908F",
  "#D7263D",
  "#662E9B",
  "#C7F464",
  "#FF6E54",
  "#8D5B4C",
  "#4ECDC4",
  "#FF9F1C",
  "#2EC4B6",
  "#5A189A",
  "#7209B7",
  "#F72585",
];

const generateColors = (count) => {
  if (count <= colorPalette.length) return colorPalette.slice(0, count);

  // If more slices than palette, generate random extras
  const extraColors = Array.from(
    { length: count - colorPalette.length },
    () => "#" + Math.floor(Math.random() * 16777215).toString(16)
  );

  return [...colorPalette, ...extraColors];
};
// -------------------------------------------------------------

const preparePieData = (allData) => {
  if (!allData || allData.length === 0) return [];

  const mainItems = allData.filter(
    (item) => item?.percentageOfOverallSales >= 5
  );
  const othersItems = allData.filter(
    (item) => item?.percentageOfOverallSales < 5
  );

  const othersPercent = othersItems.reduce(
    (sum, item) => sum + item.percentageOfOverallSales,
    0
  );

  const chartData = [
    ...mainItems.map((item) => ({
      itemName: item?.itemName,
      percentage: item?.percentageOfOverallSales,
    })),
  ];

  if (othersPercent > 0) {
    chartData.push({ itemName: "Others", percentage: othersPercent });
  }

  return chartData;
};

const ItemPercentReport = ({ categoryData }) => {
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [dateRange, setDateRange] = useState([]);

  const formatDate = (date) => date?.toLocaleDateString("en-CA");

  const fetchData = async (
    from = fromDate,
    to = toDate,
    category = selectedCategory
  ) => {
    if (!from || !to) return;

    setLoading(true);
    try {
      const response = await SalesReportService.itemPercent({
        categoryId: category?.value || null,
        fromDate: formatDate(from),
        toDate: formatDate(to),
      });
      setAllData(response || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setAllData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const today = new Date();
    const lastMonth = new Date();
    lastMonth.setMonth(today.getMonth() - 1);

    setFromDate(lastMonth);
    setToDate(today);
    setDateRange([lastMonth, today]);

    fetchData(lastMonth, today, null);
  }, []);

  useEffect(() => {
    if (fromDate && toDate) {
      fetchData(fromDate, toDate, selectedCategory);
    }
  }, [selectedCategory]);

  const categoryOption = categoryData?.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const getDateRangeDisplay = () => {
    if (!fromDate || !toDate) return "Select Date Range";

    const start = new Date(fromDate).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });

    const end = new Date(toDate).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    return `${start} - ${end}`;
  };

  const pieData = preparePieData(allData);
  const colors = generateColors(pieData.length);

  return (
    <>
      <div className="p-3 p-lg-4 mt-1">
        <Row className="mb-3 g-2">
          <Col md={4}>
            {" "}
            <div>
              <h5 className="mb-1 fw-bold text-dark">
                Item Sales Percentage Report
              </h5>
              <p className="text-muted small mb-0">{getDateRangeDisplay()}</p>
            </div>
          </Col>
          <Col md={3}>
            <Select
              isClearable
              options={categoryOption}
              value={selectedCategory}
              onChange={setSelectedCategory}
              placeholder="Filter by Category"
              className="fs-6"
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </Col>

          <Col md={3}>
            <InputGroup>
              <Flatpickr
                options={{
                  mode: "range",
                  dateFormat: "Y-m-d",
                }}
                value={dateRange}
                onChange={(dates) => {
                  if (dates.length === 2) {
                    setDateRange(dates);
                    setFromDate(dates[0]);
                    setToDate(dates[1]);
                  }
                }}
                placeholder="Select Date Range"
                className="form-control"
              />
              <InputGroupText style={{ cursor: "pointer" }}>
                <i className="bx bx-calendar"></i>
              </InputGroupText>
            </InputGroup>
          </Col>
          {/* <Col md={3}>
            <InputGroup>
              <Flatpickr
                options={{ dateFormat: "Y-m-d" }}
                value={fromDate}
                onChange={(dates) => setFromDate(dates[0])}
                className="form-control"
                placeholder="From Date"
              />
              <InputGroupText>
                <i className="bx bx-calendar"></i>
              </InputGroupText>
            </InputGroup>
          </Col>

          <Col md={3}>
            <InputGroup>
              <Flatpickr
                options={{ dateFormat: "Y-m-d" }}
                value={toDate}
                onChange={(dates) => setToDate(dates[0])}
                className="form-control"
                placeholder="To Date"
              />
              <InputGroupText>
                <i className="bx bx-calendar"></i>
              </InputGroupText>
            </InputGroup>
          </Col> */}

          <Col md={2}>
            <Button
              color="primary"
              className="w-100"
              onClick={() => fetchData()}
            >
              <i className="bx bx-search-alt me-1"></i> Generate
            </Button>
          </Col>
        </Row>

        {loading ? (
          <div className="my-3 text-center">
            <Triangle visible height="80" width="80" color="#5B71B9" />
            <h5 className="mt-1">Loading...</h5>
          </div>
        ) : pieData.length > 0 ? (
          <Card className="shadow-sm border-0">
            <CardBody>
              <PieChart
                dataColors={JSON.stringify(colors)}
                chartData={pieData}
              />
            </CardBody>
          </Card>
        ) : (
          <div className="text-center py-5">
            <i className="ri-emotion-unhappy-line display-4 text-secondary" />
            <h5 className="fw-semibold text-secondary mt-2">No Data Found</h5>
            <p className="text-muted mb-0">
              No percentage data available for the selected filters.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default ItemPercentReport;
